/**
 * Resolve MA UI identity chrome during Support Mode.
 * Prefer the impersonation handoff session over stale localStorage userInfo
 * or demo hardcodes.
 */
import {
  PROFILE_CHROME_SOURCE_KK_PROFILE,
  resolveUserInitials,
} from "../auth/profileIdentity";

export function splitDisplayName(displayName) {
  const raw = String(displayName || "").trim();
  if (!raw || /^auth0\|/i.test(raw)) {
    return { firstName: "", lastName: "" };
  }
  const parts = raw.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "" };
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

/**
 * @param {object|null} session - kk_impersonation_session
 * @param {object|null} userInfo - UserContext / localStorage profile
 */
export function resolveImpersonationDisplayIdentity(session, userInfo) {
  const fromSession = splitDisplayName(session?.userDisplayName);
  const firstName =
    fromSession.firstName ||
    String(userInfo?.firstName || "").trim() ||
    "";
  const lastName =
    fromSession.lastName ||
    String(userInfo?.lastName || "").trim() ||
    "";
  const email =
    String(session?.userEmail || "").trim() ||
    String(userInfo?.email || "").trim() ||
    "";
  const fullName = `${firstName} ${lastName}`.trim();
  const rawDisplay = String(session?.userDisplayName || "").trim();
  const safeDisplay =
    rawDisplay && !/^auth0\|/i.test(rawDisplay) ? rawDisplay : "";
  const resolvedFull = fullName || safeDisplay || "Account";
  return {
    firstName,
    lastName,
    email,
    fullName: resolvedFull,
    greetingName: firstName || "there",
    headerLabel: resolvedFull === "Account" && email ? email : resolvedFull,
    // Never derive initials from the "Account" placeholder label.
    initials: resolveUserInitials({
      firstName,
      lastName,
      email,
      fullName: fullName || safeDisplay,
    }),
  };
}

/**
 * Build a clean UserContext profile for the impersonated subject.
 * Clears prior-user fields so stale localStorage cannot leak.
 */
export function buildImpersonationUserInfo(session) {
  const identity = resolveImpersonationDisplayIdentity(session, null);
  return {
    firstName: identity.firstName,
    lastName: identity.lastName,
    email: identity.email,
    companyAvatar: null,
    avatar: null,
    companyInfo: {
      name: String(session?.businessName || "").trim() || "",
    },
    impersonatedUserSub: session?.impersonatedUserSub || null,
    masterAccountId: session?.masterAccountId || null,
    supportMode: true,
  };
}

/**
 * Selected-user canonical chrome (Support Mode) — DISPLAY ONLY.
 *
 * The impersonation JWT subject IS the selected MA user (authService prefers
 * the impersonation token), so GET/PUT /me/profile during Support Mode
 * returns the selected user's canonical kk-profile row. That is the only
 * avatar/name source the MA shell may render while impersonating — never
 * Auth0 `auth_user` / stale localStorage userInfo (which may belong to the SA
 * operator's own MA login) and never the handoff snapshot.
 *
 * UserContext.updateUserInfo stamps every chrome update made while a session
 * is active with { sub: impersonatedUserSub, impersonatedUserSub,
 * supportMode: true } so (a) persistence is scoped to the selected user's sub
 * — a later direct login by a different sub discards it — and (b) the header
 * can prove the chrome belongs to THIS session. Authorization is untouched.
 */
export function bindChromeToImpersonationSession(info, session) {
  const sub = String(session?.impersonatedUserSub || "").trim() || null;
  if (!sub) return info;
  return {
    ...info,
    sub,
    impersonatedUserSub: sub,
    supportMode: true,
  };
}

/**
 * True only when `userInfo` is kk-profile chrome bound to the CURRENT
 * impersonation session's selected user.
 */
export function isSelectedUserCanonicalChrome(userInfo, session) {
  const sub = String(session?.impersonatedUserSub || "").trim();
  if (!sub || !userInfo || typeof userInfo !== "object") return false;
  return (
    userInfo.supportMode === true &&
    userInfo.profileSource === PROFILE_CHROME_SOURCE_KK_PROFILE &&
    String(userInfo.impersonatedUserSub || "").trim() === sub
  );
}

export function persistImpersonationUserInfo(session) {
  const profile = buildImpersonationUserInfo(session);
  try {
    localStorage.setItem("userInfo", JSON.stringify(profile));
  } catch {
    /* ignore quota / private mode */
  }
  return profile;
}

export function clearPersistedUserInfo() {
  try {
    localStorage.removeItem("userInfo");
  } catch {
    /* ignore */
  }
}
